import { useNavigate } from 'react-router-dom'
import { PageTemplate } from '@/components/templates'
import { FundingRoundsTable } from './components'
import { useFundingRoundsPagination } from '@/hooks/useFundingRoundsPagination'
import { useProjectsStats } from '@/hooks/useOTC'
import { formatCompactNumber } from '@/utils'
import { CONTAINER_SIZES } from '@/constants'
import { FundingMetricCard, MiniBarChart, MiniLineChart, MiniAreaChart, MiniWaveChart } from '@/components/molecules'

const FundingRoundsScreen = () => {
  const navigate = useNavigate()
  const { data: stats, loading: statsLoading } = useProjectsStats()
  const { error } = useFundingRoundsPagination()

  return (
    <PageTemplate maxWidth={CONTAINER_SIZES.WIDE}>
      <div className="space-y-6">
        {/* Page Header - aligned left as in Figma */}
        <div className="flex flex-col items-start">
          <h1 className="font-['Plus_Jakarta_Sans'] font-semibold leading-[40px] text-[#2d2923] dark:text-[#fdfdfd] text-[30px] tracking-[-0.28px] mb-2">
            Funding Rounds
          </h1>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[24px] text-[16px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)] w-[385px]">
            Access institutional-grade project funding data
          </p>
        </div>

        {/* Metrics Cards */}
        {statsLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-[100px] bg-white dark:bg-[#121212] animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-2">
            <FundingMetricCard
              icon="projects"
              label="Total Projects"
              value={stats ? formatCompactNumber(stats.total_projects) : '—'}
              percentChange={
                stats && stats.total_projects_added_30d_pct_change
                  ? {
                      value: `${Math.abs(stats.total_projects_added_30d_pct_change).toFixed(1)}%`,
                      direction: (stats.total_projects_added_30d ?? 0) >= 0 ? 'up' : 'down',
                      color: (stats.total_projects_added_30d ?? 0) >= 0 ? 'green' : 'red',
                    }
                  : undefined
              }
              chart={<MiniAreaChart />}
            />
            <FundingMetricCard
              icon="rounds"
              label="Total Funding Rounds"
              value={stats ? formatCompactNumber(stats.total_funding_rounds ?? 0) : '—'}
              percentChange={
                stats && stats.total_funding_rounds_added_30d_pct_change
                  ? {
                      value: `${Math.abs(stats.total_funding_rounds_added_30d_pct_change).toFixed(0)}%`,
                      direction: (stats.total_funding_rounds_added_30d ?? 0) >= 0 ? 'up' : 'down',
                      color: (stats.total_funding_rounds_added_30d ?? 0) >= 0 ? 'green' : 'red',
                    }
                  : undefined
              }
              chart={<MiniWaveChart />}
            />
            <FundingMetricCard
              icon="amount"
              label="Average Funding Round Size"
              value={stats ? `$${formatCompactNumber(stats.average_funding_round_size ?? 0)}` : '—'}
              chart={<MiniBarChart />}
            />
            <FundingMetricCard
              icon="investors"
              label="Total Investors"
              value={stats ? formatCompactNumber(stats.total_investors ?? 0) : '—'}
              percentChange={
                stats && stats.total_investors_added_30d && stats.total_investors_added_30d_pct_change
                  ? {
                      value: `${stats.total_investors_added_30d} (${Math.abs(stats.total_investors_added_30d_pct_change).toFixed(1)}%)`,
                      direction: stats.total_investors_added_30d >= 0 ? 'up' : 'down',
                      color: stats.total_investors_added_30d >= 0 ? 'green' : 'red',
                    }
                  : undefined
              }
              chart={<MiniLineChart />}
            />
          </div>
        )}
        
        {/* Error message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}
        
        <FundingRoundsTable
          title="Funding Rounds"
          navigate={navigate}
          useDataHook={useFundingRoundsPagination}
          dataKey="projects"
          showPagination={true}
        />
      </div>
    </PageTemplate>
  )
}

export default FundingRoundsScreen 